import { useEffect, useState } from 'react';
import { absoluteUrl } from '../api';
import { useStore } from '../store';

export interface CapturedRequest {
  id: number;
  method: string;
  path: string;
  query: string | null;
  headers: Record<string, string | string[]>;
  body: string | null;
  bodyTruncated: boolean;
  sourceIp: string | null;
  receivedAt: number;
}

function notify(captureId: string, req: CapturedRequest): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    new Notification(`xss.cool: ${req.method} on /c/${captureId.slice(0, 8)}…`, {
      body: req.body ? req.body.slice(0, 80) : `${req.path}${req.query ? '?' + req.query : ''}`,
      tag: `xsscool-capture-${captureId}-${req.id}`,
      silent: false,
    });
  } catch {
    /* ignore */
  }
}

export function useCaptureStream(captureId: string | null): CapturedRequest[] {
  const [requests, setRequests] = useState<CapturedRequest[]>([]);
  const bumpUnread = useStore((s) => s.bumpUnreadCaptures);

  useEffect(() => {
    if (!captureId) return;
    setRequests([]);

    // Initial backfill of existing requests.
    fetch(absoluteUrl(`/api/captures/${captureId}/requests?limit=50`), {
      credentials: 'include',
    })
      .then((r) => (r.ok ? r.json() : { requests: [] }))
      .then((data: { requests: CapturedRequest[] }) => {
        setRequests(data.requests);
      })
      .catch(() => {
        /* ignore */
      });

    const url = absoluteUrl(`/c/${captureId}/stream`);
    const source = new EventSource(url, { withCredentials: true });

    source.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data) as CapturedRequest;
        let isNew = false;
        setRequests((prev) => {
          if (prev.some((r) => r.id === payload.id)) return prev;
          isNew = true;
          return [payload, ...prev].slice(0, 200);
        });
        if (isNew) {
          const state = useStore.getState();
          if (state.tab !== 'captures') bumpUnread();
          if (state.notifyOnCapture && state.tab !== 'captures') {
            notify(captureId, payload);
          }
        }
      } catch {
        /* ignore */
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects; nothing to do here.
    };

    return () => {
      source.close();
    };
  }, [captureId, bumpUnread]);

  return requests;
}

export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}
